"use client";
import type { DateRange } from "@cmp-src/calendar/types";
import { CalendarInput } from "@cmp-src/form/calendar/CalendarInput/CalendarInput";
import { Button } from "@cmp-src/primitives/button";
import type {
  DedicatedExceptionRelease,
  IDedicatedException,
  IResource,
  ISchedule
} from "@gettactic/api";
import { TextInput } from "@gettactic/components/src/form/TextInput/TextInput";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import { cn } from "@gettactic/helpers/src/css/cn";
import {
  formatScheduleTimeRange,
  getDateFromSchedule
} from "@gettactic/helpers/src/date-time/getTimeFromSchedule";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import {
  useDedicatedExceptions,
  useInvalidateDedicatedExceptions,
  useResourceReservations
} from "@gettactic/hooks";
import { format, parseISO } from "date-fns";
import { CalendarClock, X } from "lucide-react";
import React from "react";
import {
  describeRelease,
  formatDay,
  formatReleaseSpan
} from "@/features/map/helpers/dedicatedReleases";
import type { ReleaseGrantee } from "@/features/reserve/hooks/useReleaseDedicatedDesk";
import { PeopleAndTeamsPicker } from "@/features/teams/components/PeopleAndTeamsPicker";
import { api } from "@/lib/api/api";
import {
  type DedicatedExceptionsText,
  dedicatedExceptionsText,
  releaseErrorMessage,
  releaseNoun
} from "./dedicatedExceptionsText";

const DAY_FORMAT = "yyyy-MM-dd";

interface ReleaseErrorBody {
  error?: { code?: string };
}

const errorCodeOf = (result: unknown): string | undefined =>
  (result as ReleaseErrorBody | null)?.error?.code;

const bookerIdOf = (schedule: ISchedule): string | undefined =>
  schedule.slots?.[0]?.user?.id ?? schedule.organizer?.id ?? schedule.user?.id;

const bookerNameOf = (schedule: ISchedule): string =>
  schedule.slots?.[0]?.user?.name ||
  schedule.organizer?.name ||
  schedule.user?.name ||
  "Someone";

interface AffectedReservationsProps {
  schedules: ISchedule[];
  isLoading: boolean;
  isTruncated: boolean;
  error: unknown;
  /** Whose reservations these are, for the sentence. */
  whose: "the owner's" | "other people's";
  text: Pick<DedicatedExceptionsText, "checking" | "checkFailed" | "tooMany">;
}

/**
 * The reservations a change to a release cancels, named before the manager
 * agrees rather than cancelled silently.
 */
const AffectedReservations: React.FC<AffectedReservationsProps> = ({
  schedules,
  isLoading,
  isTruncated,
  error,
  whose,
  text
}) => {
  if (isLoading) {
    return <p className="text-tertiary">{text.checking}</p>;
  }
  if (error || isTruncated) {
    return (
      <p className="text-destructive">
        {error ? text.checkFailed : text.tooMany} Going ahead would cancel
        reservations without showing you which ones.
      </p>
    );
  }
  if (schedules.length === 0) {
    return (
      <p className="text-tertiary">
        None of {whose} reservations fall on these days.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-tertiary">
        This cancels {schedules.length} of {whose} reservation
        {schedules.length === 1 ? "" : "s"}:
      </p>
      <ul className="max-h-48 divide-y divide-neutral-100 overflow-y-auto rounded-md border border-neutral-200">
        {schedules.map((schedule) => (
          <li
            key={schedule.id}
            className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm"
          >
            <span className="truncate font-medium text-neutral-900">
              {bookerNameOf(schedule)}
            </span>
            <span className="flex-none text-neutral-500 text-xs">
              {format(
                parseISO(`${getDateFromSchedule(schedule.start)}T00:00:00`),
                "EEE, MMM d"
              )}
              , {formatScheduleTimeRange(schedule)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export interface DedicatedExceptionsSectionProps {
  resource: IResource;
  /** The desk's dedicated user, whose absence the releases cover. */
  ownerId: string;
  ownerName: string;
  /**
   * Every owner of the resource. Withdrawing a release cancels bookings by
   * anyone else; co-owners book their own seats, so theirs are left alone.
   */
  ownerIds: string[];
  /** The office's zone, which the release days are read in. */
  timeZone: string;
  onChanged: () => void;
}

type PendingChange =
  | { kind: "create" }
  | { kind: "delete"; exception: IDedicatedException };

/**
 * The days one owner's dedicated seat is released while they are away: list,
 * add and withdraw. Adding cancels that owner's reservations on those days;
 * withdrawing cancels those of everyone who doesn't own a seat here. Both are
 * named before confirming. On a single desk the seat is the whole desk.
 */
export const DedicatedExceptionsSection: React.FC<
  DedicatedExceptionsSectionProps
> = ({ resource, ownerId, ownerName, ownerIds, timeZone, onChanged }) => {
  const exceptions = useDedicatedExceptions(resource.id);
  const invalidate = useInvalidateDedicatedExceptions();
  const loadingToast = useLoadingToast({ loadingMessage: "Saving…" });
  const noun = releaseNoun(resource);
  const text = dedicatedExceptionsText(noun, ownerName);

  const [isAdding, setIsAdding] = React.useState(false);
  const [range, setRange] = React.useState<DateRange | undefined>(undefined);
  const [release, setRelease] =
    React.useState<DedicatedExceptionRelease>("open");
  // Whole grantees rather than ids: the chips show a name and a face, and
  // there is nothing to look them up from once the picker has handed them over.
  const [teams, setTeams] = React.useState<ReleaseGrantee[]>([]);
  const [users, setUsers] = React.useState<ReleaseGrantee[]>([]);
  const [reason, setReason] = React.useState("");
  const [pending, setPending] = React.useState<PendingChange | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);

  const startsOn = range?.from ? format(range.from, DAY_FORMAT) : "";
  const endsOn = range?.to ? format(range.to, DAY_FORMAT) : startsOn;
  const needsGrantees =
    release === "assigned" && teams.length + users.length === 0;

  // The reservations the pending change cancels: creating a release cancels
  // the owner's on those days, withdrawing one cancels everyone else's.
  const pendingSpan =
    pending?.kind === "delete"
      ? { start: pending.exception.starts_on, end: pending.exception.ends_on }
      : { start: startsOn, end: endsOn };
  const reservations = useResourceReservations({
    resourceId: resource.id,
    start: pendingSpan.start,
    end: pendingSpan.end,
    tz: timeZone,
    scope: "dedicated",
    enabled: pending !== null && !!pendingSpan.start
  });
  const affected = React.useMemo(() => {
    const all = reservations.data?.schedules ?? [];
    if (!pending) return [];
    return all.filter((schedule) => {
      const bookerId = bookerIdOf(schedule);
      return pending.kind === "create"
        ? bookerId === ownerId
        : !bookerId || !ownerIds.includes(bookerId);
    });
  }, [reservations.data, pending, ownerId, ownerIds]);

  const resetForm = () => {
    setIsAdding(false);
    setRange(undefined);
    setRelease("open");
    setTeams([]);
    setUsers([]);
    setReason("");
  };

  const afterChange = async () => {
    await invalidate();
    onChanged();
  };

  const create = async () => {
    setIsSaving(true);
    loadingToast.startLoading();
    let response: Awaited<
      ReturnType<typeof api.client.organizations.resourceDedicatedExceptionPost>
    >;
    try {
      response = await api.client.organizations.resourceDedicatedExceptionPost(
        resource.id,
        {
          starts_on: startsOn,
          ends_on: endsOn,
          release,
          reason: reason.trim(),
          dedicated_user_id: ownerId,
          teams: release === "assigned" ? teams.map((t) => t.id) : undefined,
          users: release === "assigned" ? users.map((u) => u.id) : undefined
        }
      );
    } catch {
      // A dropped connection rejects rather than answering, and leaving the
      // flag set would keep Release and withdraw disabled, with the toast
      // spinning, until the builder remounts.
      loadingToast.error(text.unreachable);
      return;
    } finally {
      setIsSaving(false);
    }
    if (!response.ok) {
      loadingToast.error(
        releaseErrorMessage(errorCodeOf(response.result), noun, ownerName)
      );
      return;
    }
    const cancelled = response.result?.cancelled_schedules ?? 0;
    loadingToast.success(text.releasedToast(cancelled));
    setPending(null);
    resetForm();
    await afterChange();
  };

  const remove = async (exception: IDedicatedException) => {
    setIsSaving(true);
    loadingToast.startLoading();
    let response: Awaited<
      ReturnType<
        typeof api.client.organizations.resourceDedicatedExceptionDelete
      >
    >;
    try {
      response =
        await api.client.organizations.resourceDedicatedExceptionDelete(
          resource.id,
          exception.id
        );
    } catch {
      loadingToast.error(
        "Couldn't reach the server. The release was not withdrawn."
      );
      return;
    } finally {
      setIsSaving(false);
    }
    if (!response.ok) {
      loadingToast.error(
        releaseErrorMessage(errorCodeOf(response.result), noun, ownerName)
      );
      return;
    }
    const cancelled = response.result?.cancelled_schedules ?? 0;
    loadingToast.success(
      cancelled > 0
        ? `Release withdrawn. ${cancelled} reservation${cancelled === 1 ? "" : "s"} cancelled.`
        : "Release withdrawn."
    );
    setPending(null);
    await afterChange();
  };

  // The resource's releases cover every seat; this section is one owner's.
  const list = (exceptions.data ?? []).filter(
    (exception) => exception.dedicated_user_id === ownerId
  );

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-md border border-neutral-200 bg-white px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 font-medium text-neutral-800 text-sm">
          <CalendarClock size={13} className="text-neutral-400" />
          Away days
        </p>
        {!isAdding && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsAdding(true)}
          >
            {text.releaseLabel}
          </Button>
        )}
      </div>
      <p className="text-[11px] text-neutral-400">{text.intro}</p>

      {exceptions.isLoading ? (
        <p className="text-neutral-400 text-xs">Loading…</p>
      ) : list.length > 0 ? (
        <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200">
          {list.map((exception) => (
            <li
              key={exception.id}
              className="flex items-start justify-between gap-2 px-2.5 py-2"
            >
              <div className="min-w-0">
                <p className="font-medium text-neutral-800 text-sm">
                  {formatReleaseSpan(exception.starts_on, exception.ends_on)}
                </p>
                <p className="truncate text-[11px] text-neutral-500">
                  Released to {describeRelease(exception)}
                  {exception.reason ? ` · ${exception.reason}` : ""}
                </p>
              </div>
              <button
                type="button"
                disabled={isSaving}
                onClick={() => setPending({ kind: "delete", exception })}
                className="flex-none rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                title="Withdraw this release"
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        !isAdding && (
          <p className="text-neutral-400 text-xs">No away days planned.</p>
        )
      )}

      {isAdding && (
        <div className="flex flex-col gap-3 rounded-md border border-neutral-200 bg-neutral-50 p-3">
          <CalendarInput
            label="Days"
            mode="range"
            variant="outlined"
            setValueOnApply
            selectedDate={range}
            onSelect={(value) => {
              if (value && !Array.isArray(value) && !(value instanceof Date)) {
                setRange(value);
              }
            }}
          />
          <div>
            <p className="mb-1 font-medium text-neutral-700 text-xs">
              Who can book it
            </p>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ["open", "Anyone"],
                  ["assigned", "Specific teams and people"]
                ] as const
              ).map(([value, label]) => (
                <Button
                  key={value}
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(release === value && "border-primary")}
                  onClick={() => setRelease(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
          {release === "assigned" && (
            <PeopleAndTeamsPicker
              users={users}
              teams={teams}
              onUsersChange={setUsers}
              onTeamsChange={setTeams}
            />
          )}
          <TextInput
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Reason, e.g. Vacation (optional)"
            maxLength={500}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={resetForm}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!startsOn || needsGrantees || isSaving}
              onClick={() => setPending({ kind: "create" })}
            >
              Release
            </Button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={
          pending?.kind === "delete"
            ? "Withdraw this release?"
            : `Release ${resource.name}?`
        }
        confirmLabel={
          pending?.kind === "delete" ? "Withdraw release" : text.releaseLabel
        }
        confirmProps={{
          disabled:
            isSaving ||
            reservations.isLoading ||
            !!reservations.error ||
            !!reservations.data?.isTruncated
        }}
        onConfirm={() => {
          if (!pending) return;
          if (pending.kind === "delete") {
            return remove(pending.exception);
          }
          return create();
        }}
        message={
          // The dialog renders its message whether or not it is open, so the
          // text is only built once there is a change to confirm.
          pending && (
            <div className="flex flex-col gap-2">
              <p className="text-tertiary">
                {pending.kind === "delete"
                  ? // Withdrawing removes the whole release, a span that has
                    // not started yet included, so the sentence names its own
                    // days rather than starting from today.
                    text.withdrawSentence(
                      formatDay(pending.exception.starts_on),
                      formatDay(pending.exception.ends_on)
                    )
                  : text.releaseSentence(
                      formatDay(startsOn),
                      formatDay(endsOn),
                      release === "open"
                    )}
              </p>
              <AffectedReservations
                schedules={affected}
                isLoading={reservations.isLoading}
                isTruncated={!!reservations.data?.isTruncated}
                error={reservations.error}
                whose={
                  pending.kind === "delete" ? "other people's" : "the owner's"
                }
                text={text}
              />
            </div>
          )
        }
      />
    </div>
  );
};
